import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSession } from '../state/session.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import { Icon } from '../components/icons.tsx';
import { EmptyState, PageHeader } from '../components/ui.tsx';
import { useVisitCoverage } from '../components/staff-visit-offers-link.tsx';
import { OFFICE_NAV_ITEMS, reachableNavItems, workHubItems, type NavGate } from '../components/nav-config.ts';

/**
 * The Work hub: the office's secondary tools on one page — Job chat, Staff,
 * Visit coverage, Inspections, Extra work and Logs — so the sidebar can keep
 * Schedule and one entry here instead of six more peers. The web counterpart
 * of the phone's More sheet, for operational tools only: Settings stays in the
 * sidebar's footer.
 *
 * Each tile is the sidebar's own entry (components/nav-config.ts) behind the
 * same gates, so the hub never offers a page the sidebar would hide, and it
 * fetches nothing of its own: the pages it links to load their own data.
 *
 * Behind `FEATURE_NAV_SECONDARY_HUB` (`company.navSecondaryHub`). With it off
 * there is no hub, and the address goes home rather than to a page the
 * navigation does not offer.
 */
export default function WorkHubPage() {
  const { t } = useTranslation();
  const { can, company } = useSession();
  const visitCoverage = useVisitCoverage();
  useDocumentTitle(t('workHub.title'));

  if (company?.navSecondaryHub !== true) return <Navigate to="/" replace />;

  // Feedback and Help are footer entries, never hub tiles: their switches do
  // not matter here.
  const gate: NavGate = { can, company, feedbackEnabled: false, helpAssistant: false, visitCoverage };
  const tools = workHubItems(gate);
  const hasSchedule = reachableNavItems(OFFICE_NAV_ITEMS, gate).some((item) => item.to === '/schedule');

  return (
    <>
      <PageHeader title={t('workHub.title')} description={t('workHub.description')} />
      {tools.length === 0 ? (
        <EmptyState title={t('workHub.emptyTitle')}>
          <p>{t('workHub.emptyBody')}</p>
          <Link className="btn btn-secondary mt-4" to={hasSchedule ? '/schedule' : '/'}>
            {hasSchedule ? t('workHub.backToSchedule') : t('workHub.backHome')}
          </Link>
        </EmptyState>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-label={t('workHub.title')}>
          {tools.map((tool) => (
            <li key={tool.to}>
              <Link
                to={tool.to}
                className="card flex h-full items-start gap-3 hover:border-[color:var(--color-brand)] focus-visible:outline-2 focus-visible:outline-offset-2"
              >
                <Icon name={tool.icon} className="mt-0.5 shrink-0 text-[color:var(--color-muted)]" />
                <span className="min-w-0">
                  <span className="block font-semibold">{t(tool.labelKey)}</span>
                  {tool.hubDescriptionKey ? (
                    <span className="mt-1 block text-sm text-[color:var(--color-muted)]">
                      {t(tool.hubDescriptionKey)}
                    </span>
                  ) : null}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
